import { Button, DatePicker, Grid, Popconfirm } from "antd";
import { CalendarPicker } from "antd-mobile";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CalendarBlank from "../../assets/images/icons/CalendarBlank";
import { CoachScheduleResponse } from "../../hooks/Coach/data";
import useGetTimezone from "../../hooks/Timezone/timezone.hooks";
import { pushGtag } from "../../plugins/utils";
import { CalendarSelectorProps } from "./data";
import { isAvailable } from "./enumerations/isAvailable";
import "./styles.scss";

const CalendarSelector: React.FC<CalendarSelectorProps> = ({
  setPanelDate,
  schedule,
  form,
  defaultOpen,
  isLoading = false,
}) => {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { t } = useTranslation();
  const { RangePicker } = DatePicker;
  const {
    toServerTimezone,
    toLocalTimezone,
    getTimezoneOffset,
    isAlreadyPassTimeLimit,
  } = useGetTimezone();

  const [open, setOpen] = useState<boolean>(false);
  const [openPopConfirm, setOpenPopConfirm] = useState<boolean>(false);
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [disableDate, setDisableDate] = useState<string[]>([]);
  const [focus, setFocus] = useState<string>("start");
  const [currentPanelDate, setCurrentPanelDate] = useState<Dayjs>();
  const [selectedRange, setSelectedRange] = useState<
    [Dayjs, Dayjs] | [undefined, undefined]
  >([undefined, undefined]);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (screens.md) {
      findPanelDate(
        currentPanelDate
          ? currentPanelDate
          : focus === "start"
          ? selectedRange[0] || dayjs()
          : selectedRange[1] || dayjs()
      );
      setOpen(true);
      return;
    }
    e.stopPropagation();
    setTimeout(() => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }, 10);
    setPopupOpen(true);
  };

  const handleRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null,
    dateStrings: [string, string]
  ) => {
    // console.debug("RangePicker onChange:", dates);
    let isRangeOverDisable = false;
    disableDate.map((date: string) => {
      if (
        dayjs(date).isAfter(dates && dates[0]) &&
        dayjs(date).isBefore(dates && dates[1])
      )
        isRangeOverDisable = true;
      return date;
    });
    // console.debug("isRangeOverDisable", isRangeOverDisable);
    if (!isRangeOverDisable) {
      if (dates && dates[0] && dates[1]) {
        setSelectedRange([dates[0], dates[1]]);
        onSelectDate([dates[0], dates[1]]);
      } else {
        setSelectedRange([undefined, undefined]);
        onSelectDate(undefined);
      }
      setOpen(false);
      setCurrentPanelDate(undefined);
    } else {
      setSelectedRange([undefined, undefined]);
      onSelectDate(undefined);
      setOpen(false);
      setOpenPopConfirm(true);
    }
  };

  const findPanelDate = (date: Dayjs) => {
    // FIXME: Should catch first day and last day in panel
    const twoMonthBefore = date
      .add(-2, "month")
      .startOf("month")
      .format("YYYY-MM-DD");
    const twoMonthAfter = date
      .add(+2, "month")
      .endOf("month")
      .format("YYYY-MM-DD");
    setPanelDate({
      start_date: toServerTimezone(twoMonthBefore)?.format(
        "YYYY-MM-DDTHH:mm:ss[Z]"
      ),
      end_date: toServerTimezone(twoMonthAfter)?.format(
        "YYYY-MM-DDTHH:mm:ss[Z]"
      ),
    });
  };

  const handleOpenChange = async (open: boolean) => {
    if (!screens.sm) {
      // Block RangePicker dropdown from opening
      setOpen(false);
    }
  };

  const handlePanelChange = (value: any) => {
    // console.debug("panelChange", value);
    const newCurrentPanelDate = dayjs(focus === "start" ? value[0] : value[1]);
    findPanelDate(newCurrentPanelDate);
    setCurrentPanelDate(newCurrentPanelDate);
  };

  const handleFocus = (event: any, { range }: any) => {
    // console.debug("range", range);
    setFocus(range);
  };

  const renderExtraFooter = () => {
    return (
      <Button
        type="link"
        onClick={() => {
          handleRangeChange(null, ["", ""]);
          pushGtag({
            event: "cancel_calendar",
          });
        }}
      >
        {t("normal.cancel")}
      </Button>
    );
  };

  const onSelectDate = (val: [Dayjs, Dayjs] | undefined) => {
    if (val) {
      const startDateString = val[0].format(
        `YYYY-MM-DD 00:00:00${getTimezoneOffset(
          process.env.REACT_APP_TIMEZONE
        )}`
      );
      const endDateString = val[1].format(
        `YYYY-MM-DD 23:59:59${getTimezoneOffset(
          process.env.REACT_APP_TIMEZONE
        )}`
      );
      form.setFieldsValue({
        start_date: toServerTimezone(startDateString)?.format(
          "YYYY-MM-DDTHH:mm:ss[Z]"
        ),
        end_date: toServerTimezone(endDateString)?.format(
          "YYYY-MM-DDTHH:mm:ss[Z]"
        ),
        date: true,
      });
    } else {
      form.setFieldsValue({
        date: undefined,
      });
    }
  };

  useEffect(() => {
    if (!schedule) return;
    // console.debug("schedule", schedule);
    const newDisableDate: any[] = [];
    schedule.map((item: CoachScheduleResponse) => {
      if (item.is_available === isAvailable.DISABLE)
        newDisableDate.push(toLocalTimezone(item.date)?.format("YYYY-MM-DD"));
      return item;
    });
    const today = toLocalTimezone(dayjs())?.format("YYYY-MM-DD");
    const set = new Set(newDisableDate);
    set.add(today);
    if (isAlreadyPassTimeLimit()) {
      const tomorrow = toLocalTimezone(dayjs().add(1, "day"))?.format(
        "YYYY-MM-DD"
      );
      set.add(tomorrow);
    }
    setDisableDate(Array.from(set));
  }, [schedule]);

  useEffect(() => {
    findPanelDate(dayjs());
  }, []);

  useEffect(() => {
    if (defaultOpen !== undefined) {
      setOpen(defaultOpen);
      return;
    }
    if (screens.md === true) setOpen(true);
  }, [screens, defaultOpen]);

  const [visibleMonth, setVisibleMonth] = useState<string>("");
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const SCROLL_TRESHOLD = 150;

  const onScroll = (scrollContainer: Element) => {
    setIsScrolling(true);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      const monthElements =
        scrollContainer.querySelectorAll("[data-year-month]");
      monthElements.forEach((el) => {
        const rect = (el as HTMLElement).getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const difference = rect.top - containerRect.top;
        if (difference < SCROLL_TRESHOLD) {
          const title = el?.getAttribute("data-year-month");
          if (title) {
            setVisibleMonth(title);
          }
          return false; // break out of forEach
        }
        setTimeout(() => {
          setIsScrolling(false);
        }, 1000);
        return true;
      });
    }, 200);
  };

  const handleCalendarConfirm = (values: [Date, Date] | null) => {
    // console.debug("dates", values);
    let isRangeOverDisable = false;
    disableDate.map((date: string) => {
      if (
        dayjs(date).isAfter(values && values[0]) &&
        dayjs(date).isBefore(values && values[1])
      )
        isRangeOverDisable = true;
      return date;
    });

    if (isRangeOverDisable) {
      setSelectedRange([undefined, undefined]);
      onSelectDate(undefined);
      setOpenPopConfirm(true);
    } else {
      if (values && values[0] && values[1]) {
        const [start, end] = values.map((d) => dayjs(d));
        console.debug(
          "Selected from mobile calendar:",
          start.format("YYYY-MM-DD"),
          end.format("YYYY-MM-DD")
        );
        setSelectedRange([start, end]);
        onSelectDate([start, end]);
      } else {
        setSelectedRange([undefined, undefined]);
        onSelectDate(undefined);
      }
    }
  };

  const shouldDisableDate = (date: Date) => {
    if (isScrolling) return true;
    const current: Dayjs = dayjs(date);
    return (
      (current && current < dayjs().endOf("day")) ||
      disableDate.includes(current.format("YYYY-MM-DD"))
    );
  };

  useEffect(() => {
    let scrollContainer: Element | null | undefined;
    let observer: MutationObserver | undefined;
    if (popupOpen === true) {
      observer = new MutationObserver(() => {
        const popup = document.querySelector(".adm-calendar-picker-popup");
        if (!popup) return;

        scrollContainer = popup.querySelector(".adm-calendar-picker-view-body");
        if (scrollContainer) {
          scrollContainer.addEventListener("scroll", () =>
            onScroll(scrollContainer as Element)
          );
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
    } else {
      if (scrollContainer)
        scrollContainer.removeEventListener("scroll", () =>
          onScroll(scrollContainer as Element)
        );
      if (observer) observer.disconnect();
    }

    return () => {
      if (scrollContainer)
        scrollContainer.removeEventListener("scroll", () =>
          onScroll(scrollContainer as Element)
        );
      if (observer) observer.disconnect();
    };
  }, [popupOpen]);

  useEffect(() => {
    if (screens.md === true) return;
    if (!visibleMonth) return;
    findPanelDate(dayjs(`${visibleMonth}-01`, "YYYY-M-DD"));
  }, [visibleMonth, screens]);

  return (
    <>
      <Popconfirm
        title={t("calendar.hint.title")}
        description={t("calendar.hint.description")}
        open={openPopConfirm}
        onConfirm={() => setOpenPopConfirm(false)}
        showCancel={false}
        okText={t("calendar.hint.ok")}
        icon={false}
      >
        <RangePicker
          disabledDate={(current) => {
            // return disableDate.includes(current.format("YYYY-MM-DD"));
            return (
              (current && current < dayjs().endOf("day")) ||
              disableDate.includes(current.format("YYYY-MM-DD"))
            );
          }}
          value={selectedRange}
          open={screens.md ? open : false}
          onClick={handleClick}
          onFocus={handleFocus}
          onOpenChange={handleOpenChange}
          onChange={handleRangeChange}
          onPanelChange={handlePanelChange}
          style={{ width: "100%" }}
          renderExtraFooter={renderExtraFooter}
          readOnly
          placeholder={[
            t("home.searchbar.date.start"),
            t("home.searchbar.date.end"),
          ]}
          prefix={
            screens.lg ? null : (
              <CalendarBlank
                style={{
                  fontSize: 24,
                  color: "var(--Neutral-dark-80)",
                }}
              />
            )
          }
          suffixIcon={
            screens.lg ? (
              <CalendarBlank
                style={{
                  fontSize: 24,
                  color: "var(--Neutral-dark-80)",
                }}
              />
            ) : null
          }
        />
        <CalendarPicker
          className="calendar-picker"
          title={t("calendar.picker.title")}
          visible={popupOpen}
          selectionMode={"range"}
          onClose={() => setPopupOpen(false)}
          onMaskClick={() => setPopupOpen(false)}
          min={new Date()}
          max={dayjs().add(5, "year").toDate()}
          shouldDisableDate={shouldDisableDate}
          onConfirm={handleCalendarConfirm}
          confirmText={t("normal.confirm")}
          renderTop={() => false}
        />
      </Popconfirm>
    </>
  );
};

export default CalendarSelector;
